import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import LabelledSwitch from "../components/labelledSwitch/LabelledSwitch";
import ImageTagsSelect from "./ImageTagsSelect";
import SelectImageProducts from "./SelectImageProducts";

export interface Image {
  readonly id: string;
  readonly name: string;
  readonly tags: string[];
  readonly products: string[];
  readonly info?: string;
  readonly source?: string;
  readonly isPrivate: boolean;
}

interface Props {
  readonly open: boolean;
  readonly image: Image | null;
  readonly onClose: () => void;
  readonly onEdit: (id: string, image: Omit<Image, "id">) => void | Promise<void>;
}

const EditImageDialog: FC<Props> = ({ open, image, onEdit, onClose }) => {
  const [tags, setTags] = useState(image?.tags ?? []);
  const [filename, setFilename] = useState(image?.name ?? "");
  const [info, setInfo] = useState(image?.info ?? "");
  const [source, setSource] = useState(image?.source ?? "");
  const [isPrivate, setIsPrivate] = useState(image?.isPrivate ?? true);
  const [imageProducts, setImageProducts] = useState(image?.products ?? []);

  useEffect(() => {
    setTags(image?.tags ?? []);
    setFilename(image?.name ?? "");
    setInfo(image?.info ?? "");
    setSource(image?.source ?? "");
    setIsPrivate(image?.isPrivate ?? true);
    setImageProducts(image?.products ?? []);
  }, [image]);

  const handleEdit = async () => {
    if (!image) {
      return;
    }
    await onEdit(image.id, {
      name: filename,
      tags,
      info: info ? info : undefined,
      source: source ? source : undefined,
      isPrivate: isPrivate,
      products: imageProducts,
    });
    onClose();
  };

  const editEnabled = !!filename;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edytuj metadane</DialogTitle>
      <DialogContent>
        <Stack
          spacing={2}
          sx={{
            minWidth: "400px",
            marginTop: 1,
          }}
        >
          <TextField label="Nazwa" fullWidth value={filename} onChange={(e) => setFilename(e.target.value)} />
          <ImageTagsSelect fullWidth selectedValues={tags} onSelectionChange={(v) => setTags(v)} />
          <TextField label="Źródło" fullWidth value={source} onChange={(e) => setSource(e.target.value)} />
          <TextField label="Info" fullWidth value={info} onChange={(e) => setInfo(e.target.value)} />
          <SelectImageProducts imageProducts={imageProducts} setImageProducts={setImageProducts} />
          <LabelledSwitch label="Prywatne" checked={isPrivate} onChange={(e) => setIsPrivate(e)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          Anuluj
        </Button>
        <AsyncLoadingButton color="primary" disabled={!editEnabled} onClick={handleEdit}>
          Zapisz
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditImageDialog;
